import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Mail, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const OTP_LEN = 6;

export default function VerifyOtp() {
    const { verifyOtp, resendOtp } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [params] = useSearchParams();

    const passedEmail = loc.state?.email || params.get("email") || "";
    const [email, setEmail] = useState(passedEmail);
    const [digits, setDigits] = useState(Array(OTP_LEN).fill(""));
    const [err, setErr] = useState("");
    const [info, setInfo] = useState(loc.state?.message || "");
    const [verifying, setVerifying] = useState(false);
    const [resending, setResending] = useState(false);
    const [cooldown, setCooldown] = useState(60);
    const inputs = useRef([]);

    useEffect(() => {
        // Focus first input on mount
        inputs.current[0]?.focus();
    }, []);

    // Resend cooldown countdown
    useEffect(() => {
        if (cooldown <= 0) return;
        const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [cooldown]);

    const onDigit = (i, v) => {
        const ch = v.replace(/\D/g, "").slice(-1);
        const next = [...digits];
        next[i] = ch;
        setDigits(next);
        if (ch && i < OTP_LEN - 1) inputs.current[i + 1]?.focus();
        if (next.every((d) => d !== "")) submit(next.join(""));
    };

    const onKey = (i, e) => {
        if (e.key === "Backspace" && !digits[i] && i > 0) {
            inputs.current[i - 1]?.focus();
        }
    };

    const onPaste = (e) => {
        const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LEN);
        if (!text) return;
        e.preventDefault();
        const next = Array(OTP_LEN).fill("");
        for (let i = 0; i < text.length; i++) next[i] = text[i];
        setDigits(next);
        const focusIdx = Math.min(text.length, OTP_LEN - 1);
        inputs.current[focusIdx]?.focus();
        if (text.length === OTP_LEN) submit(text);
    };

    const submit = async (code) => {
        const otp = code || digits.join("");
        if (otp.length !== OTP_LEN) return;
        if (!email) {
            setErr("Please enter your email.");
            return;
        }
        setErr("");
        setInfo("");
        setVerifying(true);
        const res = await verifyOtp(email, otp);
        setVerifying(false);
        if (!res.ok) {
            setErr(res.error || "Verification failed.");
            setDigits(Array(OTP_LEN).fill(""));
            inputs.current[0]?.focus();
            return;
        }
        nav(loc?.state?.next || "/dashboard");
    };

    const onResend = async () => {
        if (!email) {
            setErr("Please enter your email.");
            return;
        }
        setErr("");
        setResending(true);
        const res = await resendOtp(email);
        setResending(false);
        if (!res.ok) {
            setErr(res.error || "Couldn't resend code.");
        } else {
            setInfo("A new code has been emailed.");
            setCooldown(60);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-navy-900 grid-pattern px-4 sm:px-6 py-12 overflow-x-hidden">
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center gap-3 justify-center mb-8">
                    <div className="w-28 h-28 flex items-center justify-center">
                        <img
                            src="/gpm-logo.png"
                            alt="GPM"
                            className="w-full h-full object-contain drop-shadow-[0_0_24px_rgba(218,165,32,0.5)]"
                        />
                    </div>
                    <div className="text-left">
                        <div className="font-heading font-bold text-white text-xl leading-tight">Global Property Mart</div>
                        <div className="text-[10px] tracking-[0.22em] uppercase text-gold-500/90">One Platform · Every Need</div>
                    </div>
                </Link>

                <div className="rounded-3xl glass p-5 sm:p-8 gold-border-glow" data-testid="verify-otp-card">
                    <div className="flex items-center gap-2 text-gold-500">
                        <ShieldCheck className="w-5 h-5" />
                        <span className="text-[11px] tracking-widest uppercase font-bold">Email Verification</span>
                    </div>
                    <h1 className="font-heading text-3xl font-bold text-white mt-2">Enter the 6-digit code</h1>
                    <p className="text-white/65 text-sm mt-1">
                        We sent a code to <span className="text-gold-500 font-semibold">{email || "your email"}</span>.
                        It expires in 10 minutes.
                    </p>

                    {!passedEmail && (
                        <div className="mt-5">
                            <label className="text-xs text-white/60 mb-1 block flex items-center gap-2">
                                <Mail className="w-3.5 h-3.5" /> Email
                            </label>
                            <input
                                type="email"
                                data-testid="verify-email-input"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-navy-900 border border-white/10 rounded-md px-4 py-3 text-sm text-white"
                                placeholder="you@example.com"
                            />
                        </div>
                    )}

                    <div className="flex gap-1.5 sm:gap-2 md:gap-3 justify-center mt-6 w-full max-w-full" onPaste={onPaste} data-testid="otp-digits">
                        {digits.map((d, i) => (
                            <input
                                key={i}
                                ref={(el) => (inputs.current[i] = el)}
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                maxLength={1}
                                value={d}
                                onChange={(e) => onDigit(i, e.target.value)}
                                onKeyDown={(e) => onKey(i, e)}
                                data-testid={`otp-digit-${i}`}
                                className="flex-1 min-w-0 max-w-[3.5rem] aspect-square text-center text-lg xs:text-xl sm:text-2xl font-bold bg-navy-900 border-2 border-white/10 rounded-xl text-white focus:border-gold-500 focus:outline-none transition-colors"
                            />
                        ))}
                    </div>

                    {err && <div className="mt-4 text-sm text-red-400 text-center" data-testid="verify-otp-error">{err}</div>}
                    {info && !err && <div className="mt-4 text-sm text-emerald-400 text-center">{info}</div>}

                    <button
                        type="button"
                        disabled={verifying || digits.some((d) => !d)}
                        data-testid="verify-otp-submit"
                        onClick={() => submit()}
                        className="w-full mt-6 px-5 py-3 rounded-md bg-gold-500 text-navy-900 font-semibold hover:bg-gold-100 disabled:opacity-50"
                    >
                        {verifying ? "Verifying…" : "Verify & continue"}
                    </button>

                    <div className="mt-5 text-center text-sm text-white/65">
                        Didn&apos;t get the code?{" "}
                        {cooldown > 0 ? (
                            <span className="text-white/45">Resend in {cooldown}s</span>
                        ) : (
                            <button
                                type="button"
                                onClick={onResend}
                                disabled={resending}
                                data-testid="verify-otp-resend"
                                className="text-gold-500 font-medium hover:text-gold-100 underline-offset-2 hover:underline disabled:opacity-50"
                            >
                                {resending ? "Sending…" : "Resend code"}
                            </button>
                        )}
                    </div>

                    <div className="mt-3 text-center text-xs text-white/45">
                        Wrong email? <Link to="/register" className="text-gold-500/80 hover:text-gold-500">Sign up again</Link>
                    </div>

                    {/* Email support fallback */}
                    <a
                        href={`mailto:globalpropertymart95@gmail.com?subject=${encodeURIComponent("Help verifying my account")}&body=${encodeURIComponent(`Hi Team,\n\nI just registered on Global Property Mart with email: ${email || ""} and need help verifying.\n\nThanks.`)}`}
                        data-testid="verify-otp-email-cta"
                        className="mt-5 flex items-center gap-3 p-3.5 rounded-xl bg-gold-500/10 border border-gold-500/40 hover:bg-gold-500/15 transition-colors group"
                    >
                        <div className="w-9 h-9 rounded-full bg-gold-500 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                            <svg viewBox="0 0 24 24" className="w-4 h-4 text-navy-900" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-white">Email us if you don&apos;t receive the OTP</div>
                            <div className="text-xs text-white/60 mt-0.5">globalpropertymart95@gmail.com</div>
                        </div>
                        <span className="text-[10px] uppercase tracking-widest font-bold text-gold-500 shrink-0">Email →</span>
                    </a>
                </div>
            </div>
        </div>
    );
}
